export type { InvoiceStatus } from '@/utils/invoiceStatus'
import type { InvoiceStatus } from '@/utils/invoiceStatus'
export type { InvoiceEntity } from '@/utils/invoiceEntity'
import type { InvoiceEntity } from '@/utils/invoiceEntity'
export type { LegalCounsel } from '@/utils/legalCounsel'
import type { LegalCounsel } from '@/utils/legalCounsel'
export type { DocumentType } from '@/utils/documentType'
import type { DocumentType } from '@/utils/documentType'
import type { Evidence } from './invoiceAnalyser'

/**
 * Tax breakdown item for VAT/tax-free categories
 */
export interface TaxBreakdownItem {
  category_code: string
  category_label: string | null
  rate_percent: number | null
  tax_basis_amount: number | null
  tax_amount: number | null
}

/**
 * Column definition for custom fields
 */
export interface ColumnDefinition {
  id: string
  name: string
  type: 'text' | 'number' | 'date'
  createdAt: string
}

/**
 * Canonical invoice record in database
 */
export interface DatabaseInvoice {
  id: string
  // Base fields (from InvoiceRow)
  supplierName: string
  dateInvoice: string | null
  brutto: number | null
  netto: number | null
  rechnungsnummer: string
  comment: string
  // Additional fields from InvoiceExtraction
  currency: string | null
  projectName: string | null
  recipient: string | null
  entity: InvoiceEntity | null
  ustId: string | null
  confidence: 'high' | 'medium' | 'low' | null
  source: 'dashboard' | 'analyser' | 'email'
  // Custom fields (JSON)
  customFields: Record<string, string | number | null>
  // VAT and tax-free fields
  vatAmount: number | null
  taxFreeAmount: number | null
  taxableNetAmount: number | null
  vatBreakdown: TaxBreakdownItem[] | null
  taxFreeBreakdown: TaxBreakdownItem[] | null
  // Audit trail from the analyser (per-field evidence + plausibility warnings)
  evidence?: Evidence | null
  warnings?: string[] | null
  sourceDocumentId: string | null
  poId: string | null
  originalCurrency: string | null
  originalBrutto: number | null
  originalNetto: number | null
  originalVatAmount: number | null
  originalTaxFreeAmount: number | null
  originalTaxableNetAmount: number | null
  fxRate: number | null
  fxRateDate: string | null
  fxRateSource: string | null
  // Status & approval workflow
  status: InvoiceStatus
  approver: string | null
  approvalDate: string | null
  dueDate: string | null
  // Phase 3: expenses (EUR-normalised, original kept), service period, legal counsel
  auslagen: number | null
  originalAuslagen: number | null
  servicePeriodStart: string | null
  servicePeriodEnd: string | null
  legalCounsel: LegalCounsel | null
  // Phase 4: Storno/Gutschrift — document type + link to original invoice
  documentType: DocumentType
  originalInvoiceId: string | null
  // Metadata
  createdAt: string
  updatedAt: string
}

/**
 * Invoice input for creation/update (camelCase)
 */
export interface InvoiceInput {
  id?: string
  supplierName: string
  dateInvoice?: string | null
  brutto?: number | null
  netto?: number | null
  rechnungsnummer: string
  comment?: string
  currency?: string | null
  projectName?: string | null
  recipient?: string | null
  entity?: InvoiceEntity | null
  ustId?: string | null
  confidence?: 'high' | 'medium' | 'low' | null
  source?: 'dashboard' | 'analyser' | 'email'
  customFields?: Record<string, string | number | null>
  vatAmount?: number | null
  taxFreeAmount?: number | null
  taxableNetAmount?: number | null
  vatBreakdown?: TaxBreakdownItem[] | null
  taxFreeBreakdown?: TaxBreakdownItem[] | null
  evidence?: Evidence | null
  warnings?: string[] | null
  sourceDocumentId?: string | null
  poId?: string | null
  originalCurrency?: string | null
  originalBrutto?: number | null
  originalNetto?: number | null
  originalVatAmount?: number | null
  originalTaxFreeAmount?: number | null
  originalTaxableNetAmount?: number | null
  fxRate?: number | null
  fxRateDate?: string | null
  fxRateSource?: string | null
  status?: InvoiceStatus
  approver?: string | null
  approvalDate?: string | null
  dueDate?: string | null
  auslagen?: number | null
  originalAuslagen?: number | null
  servicePeriodStart?: string | null
  servicePeriodEnd?: string | null
  legalCounsel?: LegalCounsel | null
  documentType?: DocumentType
  originalInvoiceId?: string | null
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

/**
 * Filter params for database query
 */
export interface DatabaseFilterParams {
  supplierName?: string
  dateFrom?: string
  dateTo?: string
  source?: 'dashboard' | 'analyser' | 'email'
}

/**
 * Database statistics
 */
export interface DatabaseStats {
  totalInvoices: number
  dashboardCount: number
  analyserCount: number
  customColumnCount: number
  dbSize: number
  trashedCount?: number
}

export const TRASH_RETENTION_DAYS = 30

export interface TrashedInvoice extends DatabaseInvoice {
  deletedAt: string
  deletedBy: string | null
  deletedByName: string | null
  purgeAt: string
}
export interface TrashListResponse {
  data: TrashedInvoice[]
  total: number
  retentionDays: number
}
export interface RestoreInvoiceResponse {
  invoice: DatabaseInvoice
  duplicateIds: string[]
}
export interface PurgeResult {
  purged: number
  keptDocuments: number
}

/**
 * Export configuration
 */
export interface ExportConfig {
  format: 'xlsx' | 'csv'
  separator: 'comma' | 'period'
  includeCustomColumns: boolean
}

/**
 * Data grid row with editing state
 */
export interface GridRow extends DatabaseInvoice {
  _isEditing?: boolean
}

/**
 * Column definition for DataGrid
 */
export interface GridColDef {
  field: string
  headerName: string
  width: number
  editable: boolean
  type?: 'string' | 'number' | 'date' | 'actions'
  valueFormatter?: (value: any) => string
  renderCell?: (params: any) => React.ReactNode
}
